import { useState, useEffect, Fragment } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, Link } from 'react-router-dom';
import Select from 'react-select';
import DataTable from 'react-data-table-component';
import { Helmet, HelmetProvider } from 'react-helmet-async';

import { editarBreadcrumb } from '../../../actions/breadcrumb';
import { setAlert } from '../../../actions/alert';
import { crearPaquete } from '../../../actions/paquetes';
import { buscarProductos, buscarServicios } from '../../../actions/punto';

import { CustomValueContainer } from '../../../components/react_select/ReactSelect';

const NuevoPaquete = () => {

    let rutasBread = [
        {
            activo: false,
            nombre: 'Paquetes',
            path: '/configuracion/paquetes'
        },
        {
            activo: true,
            nombre: 'Nuevo paquete',
            path: '/configuracion/paquetes/nuevo'
        }
    ];

    const dispatch = useDispatch();
    const navigate = useNavigate();

    const { listado_productos, listado_servicios } = useSelector(state => state.punto);

    const [paqueteData, setPaqueteData] = useState({
        nombre: '',
        descripcion: '',
        busqueda_producto: null,
        busqueda_servicio: null,
        productos: [],
        servicios: []
    });

    const [tipoTab, setTipoTab] = useState(0);

    const [productosSolicitar, setProductosSolicitar] = useState([]);
    const [unidadesData, setUnidadesData] = useState([]);
    const [buscadorProductos, setBuscadorProductos] = useState(false);
    const [productoData, setProductoData] = useState(null);
    const [productosFilter, setProductosFilter] = useState([]);

    const [buscadorServicios, setBuscadorServicios] = useState(false);
    const [servicioData, setServicioData] = useState(null);
    const [serviciosFilter, setServiciosFilter] = useState([]);

    const styles_select = {
        menuPortal: base => ({ ...base, zIndex: 9999 }),
        container: (provided) => ({ ...provided }),
        valueContainer: (provided) => ({ ...provided, overflow: 'visible', marginTop: '15px' }),
        placeholder: (provided, state) => ({
            ...provided,
            position: 'absolute',
            top: state.hasValue || state.selectProps.inputValue ? -15 : '0%',
            transition: 'top 0.1s, font-size 0.1s',
            fontSize: (state.hasValue || state.selectProps.inputValue) && 13
        })
    };

    /* Columnas productos seleccionados */
    const columnsProductos = [
        {
            name: 'Nombre',
            selector: row => row.label,
            grow: 3
        },
        {
            name: 'Cantidad',
            cell: (row, index) => (
                <input
                    type="number"
                    className="form-control"
                    value={paqueteData.productos[index]?.cantidad}
                    min="0"
                    onChange={e => handleCantidadChange(e, index)}
                />
            )
        },
        {
            name: 'Unidad',
            grow: 1.5,
            cell: (row, index) => (
                <div className="input-group h-100 py-2">
                    <Select
                        className="select-group"
                        classNamePrefix="react-select"
                        placeholder="Venta"
                        options={row.unidades}
                        value={unidadesData[index]}
                        isClearable={true}
                        onChange={(value) => handleSelectUnidad(value, index)}
                        menuPortalTarget={document.body}
                        menuPlacement="auto"
                        components={{ ValueContainer: CustomValueContainer }}
                        styles={{ ...styles_select }}
                    />
                </div>
            )
        },
        {
            name: '',
            cell: (row, index) => (
                <div className="text-right w-100">
                    <button type="button" className="btn btn-sm btn-link px-2 float-end" onClick={() => handleRemoveProducto(index)}><i className="fas fa-trash text-danger"></i></button>
                </div>
            )
        }
    ];

    /* Columnas servicios */
    const columnsServicios = [
        {
            name: 'Servicio',
            cell: row => row.nombre,
            grow: 4
        },
        {
            name: 'Cantidad',
            cell: (row, index) => (
                <input
                    type="number"
                    className="form-control"
                    value={paqueteData.servicios[index]?.cantidad}
                    min="0"
                    onChange={e => handleCantidadServicioChange(e, index)}
                />
            )
        },
        {
            name: '',
            cell: (row, index) => (
                <div className="text-right w-100">
                    <button type="button" className="btn btn-sm btn-link px-2 float-end" onClick={() => handleRemoveServicio(index)}><i className="fas fa-trash text-danger"></i></button>
                </div>
            )
        }
    ];

    /* Handlers — productos */
    const handleBuscarProductos = () => {
        if (!buscadorProductos) {
            dispatch(buscarProductos({ nombre: paqueteData.busqueda_producto, codigo_barras: null, validar_existencias: false }));
        } else {
            setPaqueteData({ ...paqueteData, busqueda_producto: null });
        }

        setBuscadorProductos(!buscadorProductos);
    }

    const handleSelectProducto = (option) => {
        const productos = paqueteData.productos;
        const unidades = unidadesData;

        if (option) {
            const ya_existe = productosSolicitar.some(p => p.value == option.value);

            if (!ya_existe) {
                setProductosSolicitar([...productosSolicitar, option]);
                productos.push({ id_producto: option.value, id_unidad: null, conversion: 1, cantidad: 1 });
                unidades.push(null);
                setPaqueteData({ ...paqueteData, productos });
                setUnidadesData(unidades);
            } else {
                dispatch(setAlert('Ya tienes este producto en el paquete', 'danger'));
            }
        }

        setProductoData(null);
    }

    const handleCantidadChange = ({ target }, posicion) => {
        const productos = paqueteData.productos;

        if (target.value >= 0) {
            productos[posicion].cantidad = target.value || '';
            setPaqueteData({ ...paqueteData, productos });
        } else {
            dispatch(setAlert('No puedes ingresar cantidades negativas', 'danger'));
        }
    }

    const handleSelectUnidad = (option, posicion) => {
        const productos = paqueteData.productos;
        const unidades = unidadesData;

        unidades[posicion] = option;
        productos[posicion].id_unidad = option ? option.value : null;
        productos[posicion].conversion = option ? option.cantidad : 1;

        setUnidadesData([...unidades]);
        setPaqueteData({ ...paqueteData, productos });
    }

    const handleRemoveProducto = (posicion) => {
        const solicitar = productosSolicitar.filter((_, i) => i !== posicion);
        const productos = paqueteData.productos.filter((_, i) => i !== posicion);
        const unidades = unidadesData.filter((_, i) => i !== posicion);

        setProductosSolicitar(solicitar);
        setUnidadesData(unidades);
        setPaqueteData({ ...paqueteData, productos });
    }

    /* Handlers — servicios */
    const handleBuscarServicios = () => {
        if (!buscadorServicios) {
            dispatch(buscarServicios({ nombre: paqueteData.busqueda_servicio }));
        } else {
            setPaqueteData({ ...paqueteData, busqueda_servicio: null });
        }

        setBuscadorServicios(!buscadorServicios);
    }

    const handleSelectServicio = (option) => {
        const servicios = paqueteData.servicios;

        if (option) {
            const ya_existe = servicios.some(s => s.id_servicio == option.value);

            if (!ya_existe) {
                servicios.push({ id_servicio: option.value, nombre: option.label, cantidad: 1 });
            } else {
                dispatch(setAlert('Ya está agregado este servicio al paquete', 'danger'));
            }
        }

        setPaqueteData({ ...paqueteData, servicios });
        setServicioData(null);
    }

    const handleCantidadServicioChange = ({ target }, posicion) => {
        const servicios = paqueteData.servicios;

        if (target.value >= 0) {
            servicios[posicion].cantidad = target.value || '';
            setPaqueteData({ ...paqueteData, servicios });
        } else {
            dispatch(setAlert('No puedes ingresar cantidades negativas', 'danger'));
        }
    }

    const handleRemoveServicio = (posicion) => {
        const servicios = paqueteData.servicios;
        servicios.splice(posicion, 1);
        setPaqueteData({ ...paqueteData, servicios });
    }

    const handleInputChange = ({ target }) => {
        setPaqueteData({ ...paqueteData, [target.name]: target.value });
    }

    /* Guardar */
    const handleGuardarPaquete = async () => {
        if (!paqueteData.nombre) {
            dispatch(setAlert('El nombre del paquete es obligatorio', 'danger'));
            return;
        }

        if (paqueteData.productos.length === 0 && paqueteData.servicios.length === 0) {
            dispatch(setAlert('Debe haber al menos un producto o servicio en el paquete', 'danger'));
            return;
        }

        await dispatch(crearPaquete(paqueteData));
        await navigate('/configuracion/paquetes');
    }

    useEffect(() => {
        let options = [];
        for (var i = 0; i < listado_productos.length; i++) {
            options.push({ value: listado_productos[i].id, label: listado_productos[i].nombre, unidades: listado_productos[i].unidades });
        }
        setProductosFilter(options);
    }, [listado_productos]);

    useEffect(() => {
        let options = [];
        for (var i = 0; i < listado_servicios.length; i++) {
            options.push({ value: listado_servicios[i].id, label: listado_servicios[i].nombre });
        }
        setServiciosFilter(options);
    }, [listado_servicios]);

    useEffect(() => {
        dispatch(editarBreadcrumb(rutasBread));
    }, [rutasBread]);

    return (
        <Fragment>
            <HelmetProvider>
                <Helmet>
                    <style>{`
                        .select-group {
                            height: 100%;
                        }

                        .button-append {
                            padding: 0.39rem 0.70rem !important;
                            border-radius: 0 25% 25% 0 !important;
                            height: 100%;
                        }
                    `}</style>
                </Helmet>
            </HelmetProvider>

            <div className="row">
                <div className="col-md-12 px-4 pb-4">
                    <h3 className="mb-5 mt-5">Nuevo paquete</h3>

                    <div className="row mb-3">
                        <div className="col-md-3 form-group mb-3">
                            <div className="form-floating">
                                <input
                                    id="nombre"
                                    name="nombre"
                                    type="text"
                                    className="form-control"
                                    value={paqueteData.nombre}
                                    onChange={handleInputChange}
                                />
                                <label htmlFor="nombre">Nombre</label>
                            </div>
                        </div>

                        <div className="col-md-7 form-group mb-3">
                            <div className="form-floating">
                                <input
                                    id="descripcion"
                                    name="descripcion"
                                    type="text"
                                    className="form-control"
                                    value={paqueteData.descripcion}
                                    onChange={handleInputChange}
                                />
                                <label htmlFor="descripcion">Descripción</label>
                            </div>
                        </div>

                        <div className="col-md-2 mt-4">
                            <button className="btn btn-success float-end" onClick={handleGuardarPaquete}>Guardar</button>
                            <Link to='/configuracion/paquetes'><button className="btn btn-danger float-end me-2">Cancelar</button></Link>
                        </div>
                    </div>

                    <ul className="nav nav-pills nav-fill">
                        <li className="nav-item">
                            <div className="input-group-append">
                                <button type="button" className={'nav-link border border-primary' + (tipoTab === 0 ? ' text-black bg-primary' : '')} onClick={() => setTipoTab(0)}>Productos ({paqueteData.productos.length})</button>
                            </div>
                        </li>
                        <li className="nav-item">
                            <div className="input-group-append">
                                <button type="button" className={'nav-link border border-primary' + (tipoTab === 1 ? ' text-black bg-primary' : '')} onClick={() => setTipoTab(1)}>Servicios ({paqueteData.servicios.length})</button>
                            </div>
                        </li>
                    </ul>

                    {
                        tipoTab === 0 &&
                            <div className="productos row mt-4">
                                <div className="col-md-12 form-group mb-3">
                                    <div className="input-group">
                                        {
                                            buscadorProductos &&
                                                <div className="form-floating h-100">
                                                    <Select
                                                        name="id_producto"
                                                        className="select-group"
                                                        classNamePrefix="react-select"
                                                        placeholder="Productos"
                                                        options={productosFilter}
                                                        value={productoData}
                                                        isClearable={true}
                                                        onChange={(value) => handleSelectProducto(value)}
                                                        menuPortalTarget={document.body}
                                                        menuPlacement="auto"
                                                        components={{ ValueContainer: CustomValueContainer }}
                                                        styles={{ ...styles_select }}
                                                    />
                                                </div>
                                        }

                                        {
                                            !buscadorProductos &&
                                                <div className="form-floating">
                                                    <input
                                                        id="busqueda_producto"
                                                        name="busqueda_producto"
                                                        type="text"
                                                        className="form-control text-center"
                                                        onChange={handleInputChange}
                                                    />
                                                    <label htmlFor="busqueda_producto">Buscador de productos</label>
                                                </div>
                                        }

                                        <div className="input-group-append">
                                            <button className={'btn button-append btn-' + (buscadorProductos ? 'secondary' : 'primary')} onClick={() => handleBuscarProductos()}><i className={'fa-solid fa-' + (buscadorProductos ? 'xmark' : 'search')}></i></button>
                                        </div>
                                    </div>
                                </div>

                                <div className="col-md-12 form-group mb-3">
                                    <DataTable
                                        columns={columnsProductos}
                                        data={productosSolicitar}
                                        highlightOnHover={true}
                                    />
                                </div>
                            </div>
                    }

                    {
                        tipoTab === 1 &&
                            <div className="servicios row mt-4">
                                <div className="col-md-12 form-group mb-3">
                                    <div className="input-group">
                                        {
                                            buscadorServicios &&
                                                <div className="form-floating h-100">
                                                    <Select
                                                        name="id_servicio"
                                                        className="select-group"
                                                        classNamePrefix="react-select"
                                                        placeholder="Servicios"
                                                        options={serviciosFilter}
                                                        value={servicioData}
                                                        isClearable={true}
                                                        onChange={(value) => handleSelectServicio(value)}
                                                        menuPortalTarget={document.body}
                                                        menuPlacement="auto"
                                                        components={{ ValueContainer: CustomValueContainer }}
                                                        styles={{ ...styles_select }}
                                                    />
                                                </div>
                                        }

                                        {
                                            !buscadorServicios &&
                                                <div className="form-floating">
                                                    <input
                                                        id="busqueda_servicio"
                                                        name="busqueda_servicio"
                                                        type="text"
                                                        className="form-control text-center"
                                                        onChange={handleInputChange}
                                                    />
                                                    <label htmlFor="busqueda_servicio">Buscador de servicios</label>
                                                </div>
                                        }

                                        <div className="input-group-append">
                                            <button className={'btn button-append btn-' + (buscadorServicios ? 'secondary' : 'primary')} onClick={() => handleBuscarServicios()}><i className={'fa-solid fa-' + (buscadorServicios ? 'xmark' : 'search')}></i></button>
                                        </div>
                                    </div>
                                </div>

                                <div className="col-md-12 form-group mb-3">
                                    <DataTable
                                        columns={columnsServicios}
                                        data={paqueteData.servicios}
                                        highlightOnHover={true}
                                    />
                                </div>
                            </div>
                    }
                </div>
            </div>
        </Fragment>
    )
}

export default NuevoPaquete;
